import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Wallet, QrCode, Landmark } from "lucide-react";
import api, { inr, fmtDate, formatApiError } from "./api";
import { PageHeader, inputCls } from "./components";
import { Counter, EASE } from "@/components/Motion";

export default function AddFundsPage() {
  const { user } = useOutletContext();
  const [methods, setMethods] = useState([]);
  const [payForm, setPayForm] = useState({ method_id: "", amount: "", reference: "", note: "" });
  const [myPayments, setMyPayments] = useState([]);
  const [payBusy, setPayBusy] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signature, setSignature] = useState("");
  const [refundPolicy, setRefundPolicy] = useState(
    "Eligible refunds are processed within 0–45 working days to the original payment method."
  );

  const loadMine = () => api.get("/payments/mine").then(({ data }) => setMyPayments(data)).catch(() => {});

  useEffect(() => {
    api.get("/payment-methods").then(({ data }) => setMethods(data)).catch(() => {});
    api.get("/content").then(({ data }) => {
      if (data.refund_policy) setRefundPolicy(data.refund_policy);
    }).catch(() => {});
    loadMine();
  }, []);

  const submitPayment = async () => {
    setPayBusy(true);
    try {
      await api.post("/payments/submit", {
        method_id: payForm.method_id,
        amount: parseFloat(payForm.amount),
        reference: payForm.reference.trim(),
        note: payForm.note.trim(),
        signature: signature.trim(),
        terms_accepted: termsAccepted,
      });
      toast.success("Payment submitted for verification", {
        description: "The admin will verify your reference and credit the wallet.",
      });
      setPayForm({ method_id: "", amount: "", reference: "", note: "" });
      setTermsAccepted(false);
      setSignature("");
      loadMine();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPayBusy(false);
    }
  };

  return (
    <div data-testid="add-funds-page">
      <PageHeader title="Add Funds" sub="Fuel the engine. Pay via UPI or bank and get verified credit." />

      <div className="grid gap-6 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="relative overflow-hidden rounded-3xl border border-gold/25 bg-gradient-to-br from-gold/[0.1] via-panel to-panel p-8 sm:p-10"
          data-testid="balance-card"
        >
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-gold/10 blur-[80px]" />
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">
            <Wallet size={13} className="text-gold" /> Current balance
          </div>
          <div className="mt-4 text-5xl font-bold tracking-tight text-neutral-50 sm:text-6xl" data-testid="funds-balance">
            {user ? <Counter to={user.balance} decimals={2} prefix="₹" duration={1.6} /> : "—"}
          </div>
          <p className="mt-4 text-sm text-neutral-500">Available across every service in the catalogue.</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.12 }}
          className="space-y-6"
        >
          <div className="rounded-3xl border border-white/10 bg-panel/80 p-8 sm:p-10" data-testid="pay-card">
            <div className="mb-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">
              <QrCode size={13} className="text-gold" /> Pay via UPI / Bank
            </div>
            <div className="space-y-4">
              {methods.length === 0 ? (
                <p className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-6 text-center text-sm text-neutral-500" data-testid="pay-methods-empty">
                  No payment methods are live right now — please ask support.
                </p>
              ) : (
                methods.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setPayForm({ ...payForm, method_id: m.id })}
                    data-testid={`pay-method-${m.id}`}
                    className={`flex w-full items-start gap-4 rounded-2xl border p-5 text-left transition-all duration-300 ${
                      payForm.method_id === m.id
                        ? "border-gold bg-gold/[0.08]"
                        : "border-white/10 hover:border-gold/40"
                    }`}
                  >
                    {m.qr_image ? (
                      <img src={m.qr_image} alt={`${m.name} QR`} className="h-20 w-20 shrink-0 rounded-xl border border-white/10 object-cover" />
                    ) : (
                      <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-gold/25 bg-gold/[0.06] text-gold">
                        {m.type === "bank" ? <Landmark size={26} /> : <QrCode size={26} />}
                      </span>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-neutral-100">{m.name}</div>
                      {m.details && (
                        <div className="mt-1 select-all font-mono text-sm text-goldlight">{m.details}</div>
                      )}
                      {m.instructions && (
                        <div className="mt-1 text-xs leading-relaxed text-neutral-500">{m.instructions}</div>
                      )}
                    </div>
                  </button>
                ))
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  type="number"
                  placeholder="Amount paid (₹)"
                  value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                  className={inputCls}
                  data-testid="pay-amount-input"
                />
                <input
                  placeholder="UTR / transaction reference"
                  value={payForm.reference}
                  onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })}
                  className={inputCls}
                  data-testid="pay-reference-input"
                />
              </div>
              <input
                placeholder="Note (optional)"
                value={payForm.note}
                onChange={(e) => setPayForm({ ...payForm, note: e.target.value })}
                className={inputCls}
                data-testid="pay-note-input"
              />
              <div className="rounded-xl border border-gold/25 bg-gold/[0.05] p-4" data-testid="refund-policy-box">
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold/80">Refund policy</div>
                <p className="mt-2 text-xs leading-relaxed text-neutral-400">{refundPolicy}</p>
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3.5" data-testid="pay-terms-label">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-gold"
                  data-testid="pay-terms-checkbox"
                />
                <span className="text-xs leading-relaxed text-neutral-300">
                  I accept the{" "}
                  <Link to="/terms" target="_blank" className="font-semibold text-goldlight underline decoration-gold/40 underline-offset-2 transition-colors hover:text-gold">
                    Terms &amp; Conditions
                  </Link>{" "}
                  and the <span className="font-semibold text-goldlight">Refund Policy</span> (0–45 working days), and I confirm
                  this payment is for services on BEATKINGSMM.
                </span>
              </label>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  E-signature — type your full name
                </label>
                <input
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Your full legal name"
                  className={`${inputCls} mt-2 font-serif italic`}
                  data-testid="pay-signature-input"
                />
              </div>
              <button
                onClick={submitPayment}
                disabled={payBusy || !payForm.method_id || !(parseFloat(payForm.amount) >= 10) || payForm.reference.trim().length < 4 || !termsAccepted || signature.trim().length < 2}
                data-testid="pay-submit-button"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-4 text-sm font-bold tracking-[0.12em] text-black transition-shadow duration-300 hover:shadow-[0_8px_30px_rgb(var(--gold)_/_0.35)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {payBusy && <Loader2 size={15} className="animate-spin" />}
                SUBMIT FOR VERIFICATION
              </button>
              <p className="text-center font-mono text-[9px] tracking-[0.2em] text-neutral-600">
                ADMIN VERIFIES THE REFERENCE AND CREDITS YOUR WALLET
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-panel/80 p-8" data-testid="my-payments-card">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">My payment requests</div>
            <div className="mt-4 space-y-2">
              {myPayments.length === 0 && <p className="text-sm text-neutral-600">No requests yet.</p>}
              {myPayments.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3" data-testid={`my-payment-${p.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-neutral-200">{inr(p.amount)} · {p.method_name}</div>
                    <div className="font-mono text-[10px] text-neutral-600">REF {p.reference} · {fmtDate(p.created_at)}</div>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                    p.status === "approved"
                      ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                      : p.status === "rejected"
                        ? "border-rose-400/30 bg-rose-400/10 text-rose-300"
                        : "border-amber-400/30 bg-amber-400/10 text-amber-300"
                  }`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
